import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useFocusEffect, useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useCallback, useState } from 'react'
import { TouchableOpacity } from 'react-native'
import { mvs, ScaledSheet } from 'react-native-size-matters'
import { useDispatch, useSelector } from 'react-redux'
import { ChatNavigationItem } from '~components/ChatNavigationItem'
import IconCreateAChatAlt from '~components/IconCreateAChatAlt'
import { NavigationItem } from '~components/NavigationItem'
import { View } from '~components/Themed'
import useColumns from '~hooks/useColumns'
import usePermission from '~hooks/usePermission'
import { onSnapshot } from '~providers/firebase'
import { ApplicationState } from '~redux'
import { addRecentClass, removeRecentClass } from '~redux/recent'
import ChatService from '~services/chat'
import ClassService from '~services/class'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'
import { ExistingChat } from '~types'
import { usePagination } from '~utils/react-firebase-pagination-hooks'
import { FlashList, ListRenderItem } from '@shopify/flash-list'

export default function ClassScreen(): React.JSX.Element {
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const styles = getStyles(theme)
  const [chatsCount, setChatsCount] = useState(0)
  const [isTeacher, setIsTeacher] = useState<boolean>(false)
  const user_id = useSelector((state: ApplicationState) => state.auth.user?.uid)
  const permission_create_chat = usePermission('restrictions_max_chats_per_class', chatsCount)
  const columns = useColumns()
  const { folderId } = useLocalSearchParams<{ folderId: string }>()
  const navigation = useNavigation()
  const router = useRouter()
  const [exists, setExists] = useState(false)

  const [items, { loaded, hasMore, loadingMore, loadMore }, error] = usePagination(
    ClassService.listChats(folderId),
    {
      limit: 20,
    },
  )

  React.useEffect(() => {
    if (error) {
      dispatch(removeRecentClass(folderId))
      if (router.canGoBack()) {
        router.back()
      } else {
        router.dismissTo('/folders')
      }
    }
  }, [navigation, error, dispatch, folderId, router])

  React.useEffect(() => {
    navigation.getParent()?.setOptions({
      headerRight: () => (
        <View style={styles.navIconsContainer}>
          {isTeacher && (
            <TouchableOpacity
              onPress={() => {
                router.navigate(`/folders/${folderId}/settings`, { withAnchor: true })
              }}
              style={styles.navIconsButton}
            >
              <FontAwesomeIcon color={theme.primary} size={24} icon={['far', 'cog']} />
            </TouchableOpacity>
          )}
        </View>
      ),
    })
  }, [
    navigation,
    folderId,
    theme,
    styles,
    isTeacher,
    permission_create_chat.status,
    permission_create_chat.message,
    router,
  ])

  const back = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
    } else {
      router.dismissTo('/folders')
    }
  }, [router])

  useFocusEffect(
    useCallback(() => {
      let didCancel = false

      const subscriber = onSnapshot(
        ClassService.getClass(folderId),
        (snapshot) => {
          if (!didCancel) {
            if (typeof snapshot.exists === 'function' ? !snapshot.exists() : !snapshot.exists) {
              // If it no longer exists but it used to
              if (exists) {
                dispatch(removeRecentClass(folderId))
                back()
              }
            } else {
              setExists(true)
              const data = snapshot.data()

              const id = snapshot.id
              const name = data?.name ?? ''
              navigation.getParent()?.setOptions({
                title: name,
              })
              const isTeacher = user_id && (data?.teachers ?? []).includes(user_id)
              setIsTeacher(isTeacher)
              setChatsCount(data?.chats_count ?? 0)
              if (!didCancel) {
                dispatch(
                  addRecentClass({
                    id,
                    name,
                    role: isTeacher ? 'teacher' : 'student',
                  }),
                )
              }
            }
          }
        },
        (error) => {
          if (exists) {
            dispatch(removeRecentClass(folderId))
            back()
          }
        },
      )

      return () => {
        didCancel = true
        subscriber()
      }
    }, [folderId, exists, dispatch, back, navigation, user_id]),
  )

  const renderItem: ListRenderItem<ExistingChat | { id: string }> = ({ item }) => {
    const id = item.id

    if (id === 'create') {
      const showCreateChat = () => {
        if (permission_create_chat.status === 'allowed') {
          router.navigate(`/folders/${folderId}/new-chat`)
        } else {
          router.navigate({
            pathname: 'denied',
            params: {
              message: permission_create_chat.message,
            },
          })
        }
      }

      return (
        <NavigationItem
          title="Create a chat"
          background={
            <IconCreateAChatAlt
              style={styles.alignSelfCenter}
              width={mvs(80, 0.6)}
              height={mvs(80, 0.6)}
            />
          }
          backgroundContainerStyle={styles.altBackgroundContainerStyle}
          textContainerStyle={styles.altTextContainerStyle}
          onPress={showCreateChat}
        />
      )
    }

    return <ChatNavigationItem chat={item} role={isTeacher ? 'teacher' : 'student'} />
  }

  const data = React.useMemo(() => {
    const list = items.map(ChatService.chatFromSnapshot)
    list.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
    return [
      ...(isTeacher
        ? [
            {
              id: 'create',
            },
          ]
        : []),
      ...list,
    ]
  }, [isTeacher, items])

  return (
    <>
      <FlashList<ExistingChat | { id: string }>
        key={`class-${columns}`}
        contentContainerStyle={styles.listContainer}
        data={data}
        numColumns={columns}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
        onEndReached={() => {
          if (loaded && hasMore && !loadingMore) {
            loadMore()
          }
        }}
        onEndReachedThreshold={0.5}
      />
    </>
  )
}

const getStyles = (theme: Theme) =>
  ScaledSheet.create({
    listContainer: {
      paddingVertical: '20@mvs',
      paddingHorizontal: '10@ms',
    },
    navIconsContainer: {
      flexDirection: 'row',
      paddingEnd: 10,
      backgroundColor: 'transparent',
    },
    navIconsButton: {
      marginLeft: 15,
      padding: 10,
    },
    itemSeparator: {
      height: '16@ms',
    },
    alignSelfCenter: {
      alignSelf: 'center',
    },
    altBackgroundContainerStyle: {
      backgroundColor: theme[300],
      borderColor: theme[300],
    },
    altTextContainerStyle: {
      backgroundColor: theme[100],
      borderColor: theme[100],
    },
  })
